import {
  Boolean,
  Null,
  Number,
  Array,
  Record,
  String,
  Undefined,
  Union,
  Literal,
  Static,
  Dictionary,
} from 'runtypes';

let FreshbooksInvoiceSettingsRuntype = Record({
  allowed_gatewayids: Array(Number),
  presentation: Record({ image_logo_src: String.optional() }),
});

let LinkedAccountsRuntype = Record({
  freshbooks: Record({
    access_token: String,
    created_at: String,
    expires_at: String,
    invoice_settings: FreshbooksInvoiceSettingsRuntype.Or(Null).optional(),
    refresh_token: String,
    token_type: String,
    business_account_id: String.Or(Null).Or(Undefined),
  }).optional(),
  paymentSpring: Record({
    public_api_key: String.optional(),
    private_api_key: Boolean.optional(),
  }).optional(),
  quickbooks: Record({
    access_token: String,
    created_at: String,
    expires_at: String,
    metadata: Record({
      account_id: Union(String, Null, Undefined).optional(),
      membership_fee_item_id: Union(String, Null, Undefined).optional(),
      market_charges_item_id: Union(String, Null, Undefined).optional(),
      tax_item_id: Union(String, Null, Undefined).optional(),
      taxId: Union(String, Null, Undefined).optional(),
    }),
    realm_id: String,
    refresh_token_expires_at: String,
    refresh_token: String,
    token_type: String,
  }).optional(),
  stripe: Record({
    stripe_user_id: String,
  }).optional(),
});

let BillingSettingsRuntype = Record({
  businessInfo: String,
});

export let RGBColorRuntype = Record({
  r: Number,
  g: Number,
  b: Number,
  a: Number.optional(),
});

export let PaymentProvidersRuntype = Union(
  Literal('stripe'),
  Literal('paymentspring'),
  Literal('none'),
);

export let VendorProfileTemplateTypeRuntype = Union(
  Literal('horizontalCardSquare'),
  Literal('verticalCardSquare'),
  Literal('verticalCardRound'),
  Literal('noTemplate'),
);

export let VendorProfileTemplateRuntype = Record({
  type: VendorProfileTemplateTypeRuntype,
  imageID: String.optional(),
  nameID: String.optional(),
  descriptionID: String.optional(),
  nameColor: RGBColorRuntype.optional(),
  descriptionColor: RGBColorRuntype.optional(),
  backgroundColor: RGBColorRuntype.optional(),
});

export let ProfileSettingsRuntype = Record({
  backgroundColor: RGBColorRuntype.optional(),
  defaultDisplayByDate: Boolean.optional(),
});

export let SettingsRuntype = Record({
  invoice_provider: String.optional(),
  payment_provider: PaymentProvidersRuntype.optional(),
  approval_email: Record({
    subject: String,
    emailContent: String,
  }).optional(),
  declined_email: Record({
    subject: String,
    emailContent: String,
  }).optional(),
  waitlisted_email: Record({
    subject: String,
    emailContent: String,
  }).optional(),
  invoice_email: Record({
    subject: String,
    emailContent: String,
  }).optional(),
  alternative_payment: Record({
    enabled: Boolean,
    content: String,
  }).optional(),
  billing: BillingSettingsRuntype.optional(),
  embed_style: String.optional(),
  currency: String.optional(),
  vendorProfileTemplate: VendorProfileTemplateRuntype.optional(),
  maps: Record({
    backgroundColor: RGBColorRuntype.optional(),
  }).optional(),
  profiles: ProfileSettingsRuntype.optional(),
});

export const accountFields = {
  name: String,
  contactName: String.optional(),
  phoneNumber: String.optional(),
  email: String,
  currentSeason: String,
  linked_accounts: LinkedAccountsRuntype.optional(),
  settings: SettingsRuntype.optional(),
  system_admin: Boolean.optional(),
  suspended: Boolean.optional(),
  // TODO: Migrate `showNewFeatures` and `achOnly` to featureFlags
  showNewFeatures: Boolean.optional(),
  achOnly: Boolean.optional(),
  featureFlags: Dictionary(Boolean, String).optional(),
  // TODO: Why do we need token here?
  token: String.optional(),
  subdomain: String.optional(),
  subdomainTitle: String.optional(),
  lastLoginAt: String,
};

export let UpdateAccountRuntype = Record({
  ...accountFields,
});

export let CreateAccountRuntype = Record({
  name: String,
  contactName: String,
  phoneNumber: String,
  email: String,
  subdomain: String,
  subdomainTitle: String,
  suspended: Boolean,
  showNewFeatures: Boolean,
  achOnly: Boolean,
  // TODO: Should featureFlags be here?
  system_admin: Boolean,
});

export let AccountRuntype = Record({
  id: String,
  ...accountFields,
  createdAt: String,
  updatedAt: String,
});

export let AccountSignupRuntype = Record({
  name: String,
  marketName: String,
  phone: String,
  email: String,
  moreInfo: String.optional(),
});

export let SecretKeysRuntype = Record({
  paymentspring_private_api_key: String.Or(Null).optional(),
});

export const AccountAchOnlyResponseRuntype = Record({
  achOnly: Boolean,
});

export type AccountAchOnlyResponse = Static<
  typeof AccountAchOnlyResponseRuntype
>;

// TODO: Replace `Account` type in `types/shared/Account` with this one.
export type Account = Static<typeof AccountRuntype>;
export type UpdateAccount = Static<typeof UpdateAccountRuntype>;
export type CreateAccount = Static<typeof CreateAccountRuntype>;
export type SecretKeys = Static<typeof SecretKeysRuntype>;
